import type { VlootTcoAutoRegel } from '@fleet/shared';
import { euro } from '@fleet/shared';
import { InfoTip } from '../../components/ui/InfoTip';
import { MeerTonen } from '../../components/ui/MeerTonen';
import { PeriodeKiezer } from '../../components/ui/PeriodeKiezer';
import { Panel } from '../../components/ui/Panel';
import { AutoLink } from '../../components/ui/EntiteitLink';

const CAP = 8;

/**
 * The fleet's TCO split per car, dearest first.
 *
 * Its own card rather than a section under the chart, because it answers a
 * different question: the chart says what the fleet costs over time, this says
 * which cars carry it. And the answer is not the one the fuel-card figures give
 * — on those a petrol Polo leads, here it is the leased Peugeot with four
 * electric cars behind it, because the largest cost never reaches a fuel card.
 */
export function DuursteAutos({
  rijen,
  periodLabel,
  months,
  period,
  onPeriod,
  onSelectCar,
}: {
  rijen: VlootTcoAutoRegel[];
  periodLabel: string;
  months: string[];
  period: string;
  onPeriod: (p: string | null) => void;
  onSelectCar: (kenteken: string) => void;
}) {
  const maxAuto = Math.max(1, ...rijen.map((r) => r.totaal));

  return (
    <Panel>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111111' }}>
            Duurste auto&apos;s op TCO &mdash; {periodLabel}
          </span>
          <InfoTip
            uitlijning="links"
            tekst="Donker is vast, licht is variabel. Dit is een andere volgorde dan op tankpaskosten: daar staan de benzineauto's bovenaan, hier de dure en de geleasde."
          />
        </div>
        <PeriodeKiezer months={months} period={period} onPeriod={onPeriod} />
      </div>

      {rijen.length === 0 ? (
        <div style={{ fontSize: 13, color: '#888888' }}>Geen kosten in deze periode.</div>
      ) : (
        <MeerTonen items={rijen} cap={CAP} meer={(n) => `+${n} meer tonen`}>
          {(rijen) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rijen.map((r) => (
                <div
                  key={r.kenteken}
                  onClick={() => onSelectCar(r.kenteken)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '96px minmax(0, 1fr) 92px',
                    gap: 12,
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <AutoLink kenteken={r.kenteken} style={{ fontSize: 12.5 }} />
                  <div
                    title={`${r.auto} · ${euro(r.vast)} vast, ${euro(r.variabel)} variabel`}
                    style={{
                      background: '#F0E8DC',
                      height: 18,
                      borderRadius: 3,
                      overflow: 'hidden',
                      display: 'flex',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        background: '#2A6085',
                        width: `${Math.max(0, Math.round((r.vast / maxAuto) * 100))}%`,
                        animation: 'vsGrowW .6s cubic-bezier(.4,0,.2,1) both',
                      }}
                    />
                    <div
                      style={{
                        height: '100%',
                        background: '#BCD4DA',
                        width: `${Math.max(0, Math.round((r.variabel / maxAuto) * 100))}%`,
                        animation: 'vsGrowW .6s cubic-bezier(.4,0,.2,1) both',
                      }}
                    />
                  </div>
                  <span
                    style={{ fontSize: 13, textAlign: 'right', fontWeight: 700, color: '#111111' }}
                  >
                    {euro(r.totaal)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </MeerTonen>
      )}
    </Panel>
  );
}
